/**
 * SimplePay test prompt of 3 November 2026: what the SimplePay route draws at
 * the payment choice, and on the return page before a card payment starts.
 *
 *   §1  the "under test" label, only while the buyer is on the test allowlist
 *       (SP doc §9.3);
 *   §5  the SimplePay logo from SimplePay's logo pack (September 2026,
 *       `simplepay_w360.png`, flattened onto white because SP doc §7 says the
 *       logo may not be transparent), linked in a new tab to the payment
 *       information PDF in the page's language, then the pack's own card-brand
 *       row (Mastercard, Maestro, Visa: the start call sends `methods: CARD`);
 *   §6  SP doc §8's data-transfer notice with a required checkbox that is not
 *       pre-ticked. `payment_start` refuses a SimplePay start without it.
 *
 * Nothing here is drawn on the díjbekérő route.
 */

import type { Translate } from '../lang.ts'
import { noticeText, SIMPLEPAY_INFO_PDF } from '../simplepayNotice.ts'

export function SimplePayTestLabel({ t }: { t: Translate }): JSX.Element {
  return (
    <p className="sub" id="spTestLabel" data-simplepay="test-label" style={{ margin: '6px 0 0' }}>
      <b>
        {t(
          'A bankkártyás fizetés tesztelés alatt áll, kérjük, valós vásárláshoz ne használja.',
          "Card payment is being tested. Please don't use it for a real purchase.",
        )}
      </b>
    </p>
  )
}

export function SimplePayLogo({ t }: { t: Translate }): JSX.Element {
  return (
    <div id="spLogo" data-simplepay="logo" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
      <a
        href={SIMPLEPAY_INFO_PDF[t.lang]}
        target="_blank"
        rel="noopener noreferrer"
        data-simplepay="logo-link"
      >
        <img
          src="/simplepay/simplepay_w360.png"
          width={180}
          height={31}
          title="SimplePay - Online bankkártyás fizetés"
          alt="SimplePay vásárlói tájékoztató"
          style={{ display: 'block', background: '#ffffff' }}
        />
      </a>
      <span data-simplepay="card-brands" style={{ display: 'inline-flex', gap: 4 }}>
        <img src="/simplepay/mastercard_logo_color_new.png" width={50} height={30} alt="Mastercard" />
        <img src="/simplepay/maestro_logo_color_new.png" width={50} height={30} alt="Maestro" />
        <img src="/simplepay/visa_logo_color.png" width={50} height={30} alt="Visa" />
      </span>
    </div>
  )
}

export function SimplePayNotice(props: {
  t: Translate
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}): JSX.Element {
  const { t, checked, disabled, onChange } = props
  const text = noticeText(t.lang)
  return (
    <label className="chk" id="spNotice" data-simplepay="notice" style={{ marginTop: 10 }}>
      <input
        type="checkbox"
        id="spNoticeAccept"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      {/* The privacy URL has no break point; at 320 px it would push the page
          sideways, so the notice may break inside a word. */}
      <span style={{ overflowWrap: 'anywhere', minWidth: 0 }}>
        {text.body}
        <a href={text.link} target="_blank" rel="noopener noreferrer">
          {text.link}
        </a>
        <span className="req">*</span>
      </span>
    </label>
  )
}
